'use client';

import { use, Suspense, type CSSProperties } from 'react';
import Skeleton from '@pixum/skeleton';
import { Dialog, DialogHeader, DialogContent, DialogFooter } from '@pixum/web-dialog';
import Button, { ButtonVariants } from '@pixum/button';
import Text, { TEXT_VARIANT } from '@pixum/text';
import { CreationThumbnail } from 'components';
import type { ArticleCreationMetadata } from 'data-layer';
import styles from './ArticleDeleteDialog.module.css';

export interface ArticleDeleteDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  articleTitle: string;
  previewUrl: string;
  previewAlt: string;
  metadataPromise: Promise<ArticleCreationMetadata | null> | null;
  constants: Record<string, string>;
  /** When true, disables action buttons to indicate a deletion is in progress */
  isLoading?: boolean;
}

interface PreviewInnerProps {
  metadataPromise: Promise<ArticleCreationMetadata | null>;
  previewUrl: string;
  previewAlt: string;
}

function ArticleDeleteDialogPreview({ metadataPromise, previewUrl, previewAlt }: PreviewInnerProps) {
  const metadata = use(metadataPromise);
  const creation = metadata?.pageGroups[0]?.denormalizedCreation ?? null;

  return <CreationThumbnail creation={creation} previewUrl={previewUrl} alt={previewAlt} width={240} height={240} />;
}

const DIALOG_STYLE: CSSProperties = { '--dialog-width': 'auto' } as CSSProperties;

export function ArticleDeleteDialog({
  isOpen,
  onConfirm,
  onCancel,
  previewUrl,
  previewAlt,
  metadataPromise,
  constants,
  isLoading = false
}: ArticleDeleteDialogProps) {
  const headline = constants['i18n_cart_delete_product_headline'] ?? 'Produkt löschen';
  const body =
    constants['i18n_cart_delete_product_body'] ?? 'Möchtest du dein Produkt wirklich aus dem Warenkorb löschen?';
  const cancelLabel = constants['i18n_cart_delete_product_cancel'] ?? 'Behalten';
  const confirmLabel = constants['i18n_cart_delete_product_confirm'] ?? 'Löschen';

  const header = (
    <DialogHeader center={<Text variant={TEXT_VARIANT.HEADLINE}>{headline}</Text>} right={null} alignTextToLeft />
  );

  const content = (
    <DialogContent>
      <div className={styles['content']}>
        <p className={styles['body']}>{body}</p>
        <div className={styles['preview']}>
          {isOpen &&
            (metadataPromise !== null ? (
              <Suspense
                fallback={<Skeleton variant="rect" style={{ width: 240, height: 240, borderRadius: '16px' }} />}
              >
                <ArticleDeleteDialogPreview
                  metadataPromise={metadataPromise}
                  previewUrl={previewUrl}
                  previewAlt={previewAlt}
                />
              </Suspense>
            ) : (
              <CreationThumbnail creation={null} previewUrl={previewUrl} alt={previewAlt} width={240} height={240} />
            ))}
        </div>
      </div>
    </DialogContent>
  );

  const footer = (
    <DialogFooter
      right={
        <div className={styles['actions']}>
          <Button
            variant={ButtonVariants.TERTIARY}
            type="button"
            className={styles['actionButton']}
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={ButtonVariants.TERTIARY}
            className={styles['actionButton']}
            onClick={onConfirm}
            disabled={isLoading}
            isBusy={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    />
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      header={header}
      content={content}
      footer={footer}
      style={DIALOG_STYLE}
      showCloseButton={false}
      role="alertdialog"
    />
  );
}
